/**
 * Dev-only primitive gallery. Renders every primitive in its variants/states, shown
 * in LIGHT and DARK simultaneously (the dark column is wrapped in a `.dark` island).
 * Not linked from product nav; the route is only registered when `import.meta.env.DEV`.
 */
import { chakra } from '@chakra-ui/react';
import { useState } from 'react';
import {
  Banner,
  Button,
  Checkbox,
  Cluster,
  EmptyState,
  ErrorState,
  FieldError,
  FormField,
  Grid,
  Heading,
  Icon,
  IconButton,
  Icons,
  Link,
  LoadingState,
  Radio,
  RadioGroup,
  Select,
  SelectMenu,
  Skeleton,
  Spinner,
  Stack,
  StatusBadge,
  Surface,
  Switch,
  Text,
  TextArea,
  TextInput,
  useColorMode,
} from '../index';

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Stack gap="3">
    <chakra.p
      margin="0"
      fontFamily="body"
      fontSize="xs"
      fontWeight="semibold"
      color="text.muted"
      textTransform="uppercase"
      letterSpacing="wider"
    >
      {title}
    </chakra.p>
    {children}
  </Stack>
);

function GalleryContent() {
  const [checked, setChecked] = useState(true);
  const [radio, setRadio] = useState('a');
  const [on, setOn] = useState(true);
  const [model, setModel] = useState('opus');
  return (
    <Stack gap="8">
      <Section title="Buttons">
        <Cluster gap="3">
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Danger</Button>
        </Cluster>
        <Cluster gap="3" align="center">
          <Button size="sm">Small</Button>
          <Button size="md">Medium</Button>
          <Button size="lg">Large</Button>
        </Cluster>
        <Cluster gap="3">
          <Button iconStart={<Icon as={Icons.Plus} />}>New agent</Button>
          <Button variant="secondary" iconEnd={<Icon as={Icons.ArrowRight} />}>Next</Button>
          <Button loading loadingText="Saving…">Save</Button>
          <Button disabled>Disabled</Button>
        </Cluster>
      </Section>

      <Section title="Icon buttons">
        <Cluster gap="3">
          <IconButton aria-label="Search" icon={<Icon as={Icons.Search} />} variant="secondary" />
          <IconButton aria-label="Settings" icon={<Icon as={Icons.Settings} />} variant="ghost" />
          <IconButton aria-label="Delete" icon={<Icon as={Icons.Trash2} />} variant="danger" />
          <IconButton aria-label="More" icon={<Icon as={Icons.MoreHorizontal} />} disabled />
        </Cluster>
      </Section>

      <Section title="Typography">
        <Heading level={1} size="2xl">Operator&apos;s Console</Heading>
        <Heading level={2} size="lg">Section heading</Heading>
        <Text size="lg">Large body — monitor runs and approve high-stakes actions.</Text>
        <Text>Default body text at the base UI size.</Text>
        <Text size="sm" tone="secondary">Secondary supporting copy.</Text>
        <Text size="sm" tone="muted">Muted hint / metadata.</Text>
        <Text mono size="sm">run_01J8Z…MONO-ids-and-tokens</Text>
        <Cluster gap="4">
          <Link href="#gallery">Inline link</Link>
          <Link href="#gallery" muted>Muted link</Link>
          <Link href="#gallery" external>External ↗</Link>
        </Cluster>
      </Section>

      <Section title="Status badges">
        <Cluster gap="2">
          <StatusBadge tone="success" label="Succeeded" icon={Icons.CheckCircle2} />
          <StatusBadge tone="warning" label="Needs review" icon={Icons.AlertTriangle} />
          <StatusBadge tone="danger" label="Failed" icon={Icons.XCircle} />
          <StatusBadge tone="info" label="Running" dot />
          <StatusBadge tone="neutral" label="Draft" />
          <StatusBadge tone="brand" label="New" />
        </Cluster>
      </Section>

      <Section title="Banners">
        <Stack gap="3">
          <Banner tone="info" icon={Icons.Info}>
            Heads up — this agent has never been published.
          </Banner>
          <Banner tone="success" icon={Icons.CheckCircle2} onDismiss={() => {}}>
            Published. Your changes are live.
          </Banner>
          <Banner tone="warning" icon={Icons.AlertTriangle}>
            Full autonomy is on — the agent will act without asking.
          </Banner>
          <Banner tone="danger" icon={Icons.AlertCircle} onDismiss={() => {}}>
            The platform could not execute the action.
          </Banner>
          <Banner
            tone="brand"
            icon={Icons.Zap}
            action={<Link href="#gallery">Learn more</Link>}
          >
            A brand moment — used sparingly.
          </Banner>
        </Stack>
      </Section>

      <Section title="Surfaces & elevation">
        <Grid minChildWidth="150px" gap="3">
          {(['none', 'xs', 'sm', 'md', 'lg'] as const).map((e) => (
            <Surface key={e} tone="raised" elevation={e} bordered padding="4" radius="lg">
              <Text size="sm" weight="medium">elevation {e}</Text>
            </Surface>
          ))}
        </Grid>
        <Cluster gap="3">
          <Surface tone="subtle" padding="4" bordered><Text size="sm">subtle</Text></Surface>
          <Surface tone="inverse" padding="4"><Text size="sm" tone="inverse">inverse</Text></Surface>
        </Cluster>
      </Section>

      <Section title="Forms">
        <Stack gap="4" style={{ maxWidth: '360px' }}>
          <FormField label="Agent name" help="Shown in the runs list.">
            <TextInput placeholder="e.g. Inbox triage" defaultValue="Inbox triage" />
          </FormField>
          <FormField label="Model" required>
            <Select defaultValue="opus">
              <option value="opus">Claude Opus</option>
              <option value="sonnet">Claude Sonnet</option>
            </Select>
          </FormField>
          <FormField label="Model (listbox)" help="SelectMenu trigger — must match the native Select above.">
            <SelectMenu
              label="Model"
              value={model}
              onChange={setModel}
              minWidth="100%"
              options={[
                { value: 'opus', label: 'Claude Opus' },
                { value: 'sonnet', label: 'Claude Sonnet' },
              ]}
            />
          </FormField>
          <FormField label="System prompt">
            <TextArea placeholder="You are…" rows={3} />
          </FormField>
          <FormField label="Webhook URL" error="Enter a valid https URL.">
            <TextInput defaultValue="notaurl" />
          </FormField>
          <FieldError>Standalone field error.</FieldError>
        </Stack>
        <Cluster gap="6" align="start">
          <Stack gap="2">
            <Checkbox label="Unchecked" />
            <Checkbox label="Checked" checked={checked} onChange={(e) => setChecked(e.currentTarget.checked)} />
            <Checkbox label="Indeterminate" indeterminate />
            <Checkbox label="Disabled" disabled />
          </Stack>
          <RadioGroup name="gallery-radio" aria-label="Example" value={radio} onValueChange={setRadio}>
            <Radio value="a" label="Option A" />
            <Radio value="b" label="Option B" />
            <Radio value="c" label="Disabled" disabled />
          </RadioGroup>
          <Stack gap="2">
            <Switch label="Off" />
            <Switch label="On" checked={on} onChange={(e) => setOn(e.currentTarget.checked)} />
            <Switch label="Disabled" disabled />
          </Stack>
        </Cluster>
      </Section>

      <Section title="Spinners & icons">
        <Cluster gap="4" align="center">
          <Spinner size="xs" />
          <Spinner size="sm" />
          <Spinner size="md" />
          <Spinner size="lg" />
          <Icon as={Icons.Bot} size="lg" tone="action" />
          <Icon as={Icons.ShieldCheck} size="lg" tone="success" />
          <Icon as={Icons.Zap} size="lg" tone="brand" />
        </Cluster>
      </Section>

      <Section title="Feedback states">
        <Grid minChildWidth="280px" gap="4">
          <Surface tone="raised" bordered radius="lg">
            <EmptyState
              icon={Icons.Inbox}
              title="No runs yet"
              description="Agents you start will show up here."
              action={<Button size="sm" iconStart={<Icon as={Icons.Play} />}>Start a run</Button>}
              tone="brand"
            />
          </Surface>
          <Surface tone="raised" bordered radius="lg">
            <ErrorState description="We couldn't load runs." onRetry={() => {}} />
          </Surface>
          <Surface tone="raised" bordered radius="lg" padding="6">
            <LoadingState label="Loading runs…" />
          </Surface>
          <Surface tone="raised" bordered radius="lg" padding="6">
            <Stack gap="3">
              <Skeleton variant="text" width="40%" />
              <Skeleton height="1rem" />
              <Skeleton height="1rem" width="80%" />
            </Stack>
          </Surface>
        </Grid>
      </Section>
    </Stack>
  );
}

function Panel({ mode }: { mode: 'light' | 'dark' }) {
  return (
    <chakra.div className={mode} bg="surface.subtle" flex="1" minWidth="0">
      <Surface tone="subtle" padding="6">
        <Stack gap="6">
          <Cluster gap="2" align="center">
            <chakra.span
              boxSize="2.5"
              borderRadius="full"
              bg={mode === 'dark' ? 'action.primary' : 'brand.solid'}
            />
            <Text weight="semibold">{mode === 'light' ? 'Light' : 'Dark'} mode</Text>
          </Cluster>
          <GalleryContent />
        </Stack>
      </Surface>
    </chakra.div>
  );
}

export function Gallery() {
  const { colorMode, toggleColorMode } = useColorMode();
  return (
    <chakra.div minHeight="100vh" bg="surface.subtle">
      <chakra.header
        position="sticky"
        top="0"
        zIndex="docked"
        bg="surface.default"
        borderBottomWidth="1px"
        borderColor="border.default"
        px="6"
        py="3"
      >
        <Cluster gap="3" justify="between">
          <Cluster gap="2" align="center">
            <Icon as={Icons.Component} tone="action" />
            <Heading level={1} size="md">UI Primitives — Gallery</Heading>
          </Cluster>
          <Button
            size="sm"
            variant="secondary"
            iconStart={<Icon as={colorMode === 'dark' ? Icons.Sun : Icons.Moon} />}
            onClick={toggleColorMode}
          >
            Toggle app theme ({colorMode})
          </Button>
        </Cluster>
      </chakra.header>
      <chakra.div display="flex" flexWrap="wrap" alignItems="stretch">
        <Panel mode="light" />
        <Panel mode="dark" />
      </chakra.div>
    </chakra.div>
  );
}
